import { useEffect, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { speak, chime, urgentAlarm } from "@/lib/voice";
import { toast } from "sonner";

/**
 * Per-user notifications. Consumes the same SSE channel used by
 * useConversations — but filters only events of type "notification" and
 * dispatches alarms/TTS. Uses its own EventSource so the hook is mountable
 * anywhere in the app without coupling.
 *
 * On mount, also drains any queued notifications from GET /api/notifications/me
 * (in case the user just opened the tab while alerts were stacking up).
 */
export function useUserNotifications(userId, fullName) {
  const seenRef = useRef(new Set());
  const swRegRef = useRef(null);
  const nameRef = useRef(fullName || "");
  const esRef = useRef(null);

  useEffect(() => {
    nameRef.current = fullName || "";
  }, [fullName]);

  useEffect(() => {
    if (!userId) return;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration("/pwa-sw.js").then((r) => {
        // Fall back to any registration at root scope when the script
        // URL lookup fails (older clients with notify-sw still active).
        swRegRef.current = r || null;
        if (!swRegRef.current) {
          navigator.serviceWorker.ready.then((reg) => {
            swRegRef.current = reg;
          }).catch(() => {});
        }
      });
    }

    const dispatch = (notifId, n) => {
      if (seenRef.current.has(notifId)) return;
      seenRef.current.add(notifId);

      const title = n.title || "New notification";
      const body = n.body || "";
      let showable = true;
      if (n.created_at) {
        const age = Date.now() - new Date(n.created_at).getTime();
        if (Number.isFinite(age) && age > 10 * 60 * 1000) showable = false;
      }

      if (showable) {
        const isComplaint =
          (n.type || "").toLowerCase() === "complaint" ||
          (n.kind || "").toLowerCase() === "complaint";
        try {
          if (isComplaint) {
            // Per-agent ask: skip the on-screen toast for complaints to avoid
            // distracting the agent. Audio alarm + voice announcement +
            // browser/native notification still fire so it cannot be missed.
          } else {
            toast.success(title, { description: body, duration: 8000 });
          }
        } catch (_) {}
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            if (swRegRef.current && swRegRef.current.active) {
              swRegRef.current.active.postMessage({
                type: "NOTIFY",
                title,
                body,
                tag: `rodbez-notif-${notifId}`,
                url: "/",
              });
            } else {
              const notif = new Notification(title, { body, tag: `rodbez-notif-${notifId}` });
              notif.onclick = () => {
                try { window.focus(); } catch (_) {}
                notif.close();
              };
            }
          }
        } catch (_) {}
        try {
          const firstName = (nameRef.current || "").trim().split(/\s+/)[0] || "there";
          if (isComplaint) {
            urgentAlarm();
            const customer = n.customer || "a customer";
            const phrase = `Complaint alert! Customer ${customer} has raised a complaint. Please check the inbox.`;
            setTimeout(async () => {
              const ok = await speak(phrase, { rate: 1.05, pitch: 1.1 });
              if (!ok) urgentAlarm();
            }, 1200);
          } else {
            const isAssignment = (n.type || "").toLowerCase().includes("assign") ||
              (title || "").toLowerCase().includes("assign") ||
              (n.kind || "").toLowerCase().includes("assign");
            if (isAssignment) {
              const phrase = `Hello ${firstName}, you have a new WhatsApp chat assigned.`;
              chime();
              setTimeout(async () => {
                const ok = await speak(phrase);
                if (!ok) chime(680, 200, 0.12);
              }, 350);
            } else {
              chime();
            }
          }
        } catch (_) {}
      }

      api.delete(`/notifications/${notifId}`).catch(() => {});
    };

    // 1) Initial drain — fetch any queued notifications from Mongo
    (async () => {
      try {
        const { data } = await api.get("/notifications/me");
        (data?.notifications || []).forEach((n) => dispatch(n.id, n));
      } catch (_) {}
    })();

    // 2) Live via SSE — open our own connection (lightweight; broker de-dupes
    // subscribers per user so multiple tabs all get their own events).
    const openStream = () => {
      const token = localStorage.getItem("wh_token");
      if (!token) return;
      try { esRef.current?.close(); } catch (_) {}
      const url = `${API_BASE}/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;
      es.onmessage = (evt) => {
        try {
          const { type, data } = JSON.parse(evt.data);
          if (type === "notification" && data && data.id) dispatch(data.id, data);
          else if (type === "play_review_alert" && data) {
            // De-dup so reconnects don't replay the same alert
            const alertId = `play-${data.review_id}`;
            if (seenRef.current.has(alertId)) return;
            seenRef.current.add(alertId);
            try { urgentAlarm(); } catch (_) {}
            const stars = Math.max(1, Math.min(5, Number(data.rating) || 1));
            const author = data.author || "A customer";
            const snippet = (data.text || "").slice(0, 80);
            try {
              speak(
                `New ${stars} star review from ${author} on Play Store. ${snippet}`,
              );
            } catch (_) {}
            try {
              toast.error(`${stars}★ Play Store review from ${author}`, {
                description: snippet,
                duration: 12000,
              });
            } catch (_) {}
          }
        } catch (_) {}
      };
      es.onerror = () => {
        try { es.close(); } catch (_) {}
        // EventSource auto-reconnects, but we also manually retry for reliability
        setTimeout(() => openStream(), 3000);
      };
    };
    openStream();

    return () => {
      try { esRef.current?.close(); } catch (_) {}
    };
  }, [userId]);
}
